import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Query, useMutation, useApolloClient } from 'react-apollo';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Typography from '@material-ui/core/Typography';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@material-ui/icons/RadioButtonChecked';
import Button from 'components/common/Button';
import { UPCOMING_MEETINGS } from 'graphql/admin/users';
import MeetingFeedbackModal from 'modules/lc/timeline/MeetingFeedbackModal';
import { toUserTimezone } from '../../utils';
import MaterialTable from 'material-table';
import moment from 'moment';
import { ROLES } from 'constant/userTypes';
import { UPDATE_MILESTONE_EXERCISE } from 'graphql/student/milestones';
import { GET_STUDENT } from 'graphql/lc/students';
import useSnack from 'hooks/useSnack';
import { ADD_CREDIT } from 'graphql/credits';

const MeetingDoneModal = ({ onClose }) => {
  const [showMeetings, setShowMeetings] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [updateTask] = useMutation(UPDATE_MILESTONE_EXERCISE);
  const [subtractCredit] = useMutation(ADD_CREDIT);
  const client = useApolloClient();
  const [student, setStudent] = useState(null);
  const [attendees, setAttendees] = useState([]);

  const user = useSelector((s) => s.user.user);
  const snackbar = useSnack();

  useEffect(() => {
    if (selected) {
      client
        .query({
          query: GET_STUDENT,
          variables: {
            user_id: selected.user_id,
          },
        })
        .then((resp) => {
          const student = resp.data.users[0];
          setStudent(student);
          setAttendees(getAttendeesOptions(student));
        });
    }
  }, [selected]);

  const start = useMemo(() => {
    return moment()
      .subtract(2, 'd')
      .toJSON();
  }, []);
  const end = useMemo(() => {
    return moment()
      .add(2, 'd')
      .toJSON();
  }, []);

  const onSubmitFeedback = async (values) => {
    try {
      await updateTask({
        variables: {
          id: selected.id,
          input: values,
        },
      });
      await subtractCredit({
        variables: {
          input: {
            credit_type: selected.exercise.meeting_type,
            user_id: student.user_id,
            amount: -1,
            description: 'Credit usage',
          },
        },
      });
      snackbar.success('Feedback submitted successfully');
      onClose();
    } catch (e) {
      snackbar.error(e.message);
    }
  };

  const getAttendeesOptions = (student) => {
    const attendeesOptions = [];
    attendeesOptions.push({
      label: `${student.user_first_name} ${student.user_last_name} (student)`,
      value: student.user_id,
    });
    student.guardians[0]?.guardian?.user_first_name &&
      attendeesOptions.push({
        label: `${student.guardians.length &&
          student.guardians[0]?.guardian?.user_first_name} ${student.guardians
            .length && student.guardians[0]?.guardian?.user_last_name} (parent)`,
        value:
          student.guardians.length && student.guardians[0]?.guardian?.user_id,
      });
    student.guardians[1]?.guardian?.user_first_name &&
      attendeesOptions.push({
        label: `${student.guardians.length > 1 &&
          student.guardians[1]?.guardian?.user_first_name} ${student.guardians
            .length > 1 &&
          student.guardians[1]?.guardian?.user_last_name} (parent)`,
        value:
          student.guardians.length > 1 &&
          student.guardians[1]?.guardian?.user_id,
      });
    attendeesOptions.push({
      label: `${student.hc_user.user_first_name} ${student.hc_user.user_last_name} (HC)`,
      value: student.hc_id,
    });
    attendeesOptions.push({
      label: `${student.lc_user.user_first_name} ${student.lc_user.user_last_name} (CC)`,
      value: student.lc_id,
    });
    attendeesOptions.push({
      label: `${student.admin.user_first_name} ${student.admin.user_last_name} (Program Supervisor)`,
      value: student.admin.user_id,
    });
    return attendeesOptions;
  };

  const onSelectMeeting = async () => {
    await updateTask({
      variables: {
        id: selected.id,
        input: {
          status: 'done',

        },
      },
    });
    selected.status = 'done';
    setSelected(selected);
    setShowFeedback(true);
  };

  const onSetNoShow = async () => {
    await updateTask({
      variables: {
        id: selected.id,
        input: {
          status: 'noshow',
          completed_at: toUserTimezone(new Date())

        },
      },
    });
    await subtractCredit({
      variables: {
        input: {
          credit_type: selected.exercise.meeting_type,
          user_id: student.user_id,
          amount: -1,
          description: 'Credit usage',
        },
      },
    });
    snackbar.info('Meeting marked as no show');
    onClose();
  };

  if (showFeedback && student) {
    return (
      <MeetingFeedbackModal
        onClose={onClose}
        options={attendees}
        student={student}
        task={selected}
        onSubmit={onSubmitFeedback}
      />
    );
  }

  if (showMeetings) {
    const coach = {};
    if (user.type === ROLES.LC) {
      coach.lc_id = { _eq: user.id };
    } else if (user.type === ROLES.HC) {
      coach.hc_id = { _eq: user.id };
    } else if (user.type === ROLES.FA) {
      coach.fa_id = { _eq: user.id };
    }

    return (
      <Dialog open fullWidth maxWidth="md">
        <DialogContent>
          <Query
            query={UPCOMING_MEETINGS}
            variables={{
              start,
              end,
              coach,
            }}
          >
            {({ loading, error, data }) => {
              if (error) return `Error! ${error.message}`;

              const columns = [
                {
                  title: 'Student name',
                  render: (row) =>
                    `${row.user.name} ${row.user.user_last_name}`,
                },
                { title: 'Meeting', field: 'exercise.name' },
                {
                  title: 'Scheduled at',
                  field: 'due_date',
                  render: (row) =>
                    toUserTimezone(row.due_date, user.timezone).format('LLL'),
                },
                {
                  title: 'Selected',
                  render: (row) => {
                    if (selected?.id === row.id) {
                      return <RadioButtonCheckedIcon color="primary" />;
                    }
                    return <RadioButtonUncheckedIcon />;
                  },
                },
              ];
              return (
                <MaterialTable
                  isLoading={loading}
                  columns={columns}
                  data={data?.rows}
                  options={{
                    search: false,
                    paging: false,
                    toolbar: false,
                  }}
                  onRowClick={(event, rowData) => {
                    setSelected(rowData);
                  }}
                  localization={{
                    body: {
                      emptyDataSourceMessage: 'No meetings found',
                    },
                  }}
                />
              );
            }}
          </Query>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMeetings(false)}>Cancel</Button>
          <Button disabled={!selected} color="secondary" onClick={onSetNoShow}>
            Mark No-Show
          </Button>
          <Button
            disabled={!selected}
            color="primary"
            variant="contained"
            onClick={onSelectMeeting}
          >
            Mark as done
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open>
      <DialogTitle>Meeting Summary</DialogTitle>
      <DialogContent>
        <Typography>
          Can we consider this meeting DONE?
          <br />
          *On the student's Timeline, we will label this session as completed.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          color="primary"
          onClick={() => {
            setShowMeetings(true);
            localStorage.removeItem('meeting type');
          }}
        >
          Yes, mark as DONE
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MeetingDoneModal;
